/*
 * PKI.Ciphers — the legacy crypto WebCrypto refuses to do.
 *
 * WebCrypto covers AES, PBKDF2 and the modern hashes, which is everything a
 * file produced by OpenSSL 3 needs. It is not what people have on disk. Real
 * inboxes are full of:
 *   - .pfx from Windows / IIS  → PBE-SHA1-RC2-40  (RC2: not in WebCrypto)
 *   - .p12 from old keytool    → PBE-SHA1-3DES    (3DES: removed from WebCrypto)
 *   - .key from an old nginx   → "Proc-Type: 4,ENCRYPTED" + EVP_BytesToKey
 * Every online converter that leans on WebCrypto alone rejects all three, which
 * is why "your tool says my file is invalid but openssl opens it fine" is the
 * standard complaint about this whole tool category. So the missing primitives
 * live here.
 *
 * These are legacy-compat only and are never used to protect anything we
 * generate — our own output is AES-256 + PBKDF2-SHA256.
 *
 * MD5 and SHA-1 are implemented synchronously because the KDFs iterate them
 * thousands of times; awaiting crypto.subtle per iteration would turn a 20 ms
 * unwrap into a visible stall.
 */
(function (root) {
    'use strict';

    var Asn1 = (root && root.PKI && root.PKI.Asn1) ||
        (typeof require !== 'undefined' ? require('./asn1.js') : null);

    var concat = Asn1.concat, u8 = Asn1.u8;

    /* ═══ MD5 (legacy KDF only) ═════════════════════════════════════ */

    var MD5_S = [7,12,17,22,7,12,17,22,7,12,17,22,7,12,17,22,
                 5,9,14,20,5,9,14,20,5,9,14,20,5,9,14,20,
                 4,11,16,23,4,11,16,23,4,11,16,23,4,11,16,23,
                 6,10,15,21,6,10,15,21,6,10,15,21,6,10,15,21];
    var MD5_K = (function () {
        var k = new Uint32Array(64);
        for (var i = 0; i < 64; i++) k[i] = (Math.floor(Math.abs(Math.sin(i + 1)) * 4294967296)) >>> 0;
        return k;
    })();

    function md5(input) {
        var msg = u8(input), origLen = msg.length;
        var padLen = ((origLen + 8) >> 6 << 6) + 64;
        var buf = new Uint8Array(padLen);
        buf.set(msg);
        buf[origLen] = 0x80;
        var bitLen = origLen * 8;
        var dv = new DataView(buf.buffer);
        dv.setUint32(padLen - 8, bitLen >>> 0, true);
        dv.setUint32(padLen - 4, Math.floor(bitLen / 4294967296), true);

        var a0 = 0x67452301, b0 = 0xefcdab89, c0 = 0x98badcfe, d0 = 0x10325476;
        for (var off = 0; off < padLen; off += 64) {
            var M = new Uint32Array(16);
            for (var j = 0; j < 16; j++) M[j] = dv.getUint32(off + j * 4, true);
            var A = a0, B = b0, C = c0, D = d0;
            for (var i = 0; i < 64; i++) {
                var F, g;
                if (i < 16) { F = (B & C) | (~B & D); g = i; }
                else if (i < 32) { F = (D & B) | (~D & C); g = (5 * i + 1) % 16; }
                else if (i < 48) { F = B ^ C ^ D; g = (3 * i + 5) % 16; }
                else { F = C ^ (B | ~D); g = (7 * i) % 16; }
                F = (F + A + MD5_K[i] + M[g]) >>> 0;
                A = D; D = C; C = B;
                B = (B + ((F << MD5_S[i]) | (F >>> (32 - MD5_S[i])))) >>> 0;
            }
            a0 = (a0 + A) >>> 0; b0 = (b0 + B) >>> 0;
            c0 = (c0 + C) >>> 0; d0 = (d0 + D) >>> 0;
        }
        var out = new Uint8Array(16), odv = new DataView(out.buffer);
        odv.setUint32(0, a0, true); odv.setUint32(4, b0, true);
        odv.setUint32(8, c0, true); odv.setUint32(12, d0, true);
        return out;
    }

    /* ═══ SHA-1 (legacy KDF only) ═══════════════════════════════════ */

    function sha1(input) {
        var msg = u8(input), origLen = msg.length;
        var padLen = ((origLen + 8) >> 6 << 6) + 64;
        var buf = new Uint8Array(padLen);
        buf.set(msg);
        buf[origLen] = 0x80;
        var dv = new DataView(buf.buffer);
        var bitLen = origLen * 8;
        dv.setUint32(padLen - 8, Math.floor(bitLen / 4294967296));
        dv.setUint32(padLen - 4, bitLen >>> 0);

        var h0 = 0x67452301, h1 = 0xEFCDAB89, h2 = 0x98BADCFE, h3 = 0x10325476, h4 = 0xC3D2E1F0;
        var w = new Uint32Array(80);
        for (var off = 0; off < padLen; off += 64) {
            for (var i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4);
            for (i = 16; i < 80; i++) {
                var t = w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16];
                w[i] = ((t << 1) | (t >>> 31)) >>> 0;
            }
            var a = h0, b = h1, c = h2, d = h3, e = h4;
            for (i = 0; i < 80; i++) {
                var f, k;
                if (i < 20) { f = (b & c) | (~b & d); k = 0x5A827999; }
                else if (i < 40) { f = b ^ c ^ d; k = 0x6ED9EBA1; }
                else if (i < 60) { f = (b & c) | (b & d) | (c & d); k = 0x8F1BBCDC; }
                else { f = b ^ c ^ d; k = 0xCA62C1D6; }
                var tmp = (((a << 5) | (a >>> 27)) + f + e + k + w[i]) >>> 0;
                e = d; d = c; c = ((b << 30) | (b >>> 2)) >>> 0; b = a; a = tmp;
            }
            h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0;
            h3 = (h3 + d) >>> 0; h4 = (h4 + e) >>> 0;
        }
        var out = new Uint8Array(20), odv = new DataView(out.buffer);
        [h0, h1, h2, h3, h4].forEach(function (h, i) { odv.setUint32(i * 4, h); });
        return out;
    }

    /* ═══ DES / 3DES ════════════════════════════════════════════════ */

    var PC1 = [57,49,41,33,25,17,9,1,58,50,42,34,26,18,10,2,59,51,43,35,27,19,11,3,
               60,52,44,36,63,55,47,39,31,23,15,7,62,54,46,38,30,22,14,6,61,53,45,37,29,21,13,5,28,20,12,4];
    var PC2 = [14,17,11,24,1,5,3,28,15,6,21,10,23,19,12,4,26,8,16,7,27,20,13,2,
               41,52,31,37,47,55,30,40,51,45,33,48,44,49,39,56,34,53,46,42,50,36,29,32];
    var IP = [58,50,42,34,26,18,10,2,60,52,44,36,28,20,12,4,62,54,46,38,30,22,14,6,64,56,48,40,32,24,16,8,
              57,49,41,33,25,17,9,1,59,51,43,35,27,19,11,3,61,53,45,37,29,21,13,5,63,55,47,39,31,23,15,7];
    var FP = [40,8,48,16,56,24,64,32,39,7,47,15,55,23,63,31,38,6,46,14,54,22,62,30,37,5,45,13,53,21,61,29,
              36,4,44,12,52,20,60,28,35,3,43,11,51,19,59,27,34,2,42,10,50,18,58,26,33,1,41,9,49,17,57,25];
    var E = [32,1,2,3,4,5,4,5,6,7,8,9,8,9,10,11,12,13,12,13,14,15,16,17,
             16,17,18,19,20,21,20,21,22,23,24,25,24,25,26,27,28,29,28,29,30,31,32,1];
    var P = [16,7,20,21,29,12,28,17,1,15,23,26,5,18,31,10,2,8,24,14,32,27,3,9,19,13,30,6,22,11,4,25];
    var SHIFTS = [1,1,2,2,2,2,2,2,1,2,2,2,2,2,2,1];
    var SBOX = [
        [14,4,13,1,2,15,11,8,3,10,6,12,5,9,0,7,0,15,7,4,14,2,13,1,10,6,12,11,9,5,3,8,
         4,1,14,8,13,6,2,11,15,12,9,7,3,10,5,0,15,12,8,2,4,9,1,7,5,11,3,14,10,0,6,13],
        [15,1,8,14,6,11,3,4,9,7,2,13,12,0,5,10,3,13,4,7,15,2,8,14,12,0,1,10,6,9,11,5,
         0,14,7,11,10,4,13,1,5,8,12,6,9,3,2,15,13,8,10,1,3,15,4,2,11,6,7,12,0,5,14,9],
        [10,0,9,14,6,3,15,5,1,13,12,7,11,4,2,8,13,7,0,9,3,4,6,10,2,8,5,14,12,11,15,1,
         13,6,4,9,8,15,3,0,11,1,2,12,5,10,14,7,1,10,13,0,6,9,8,7,4,15,14,3,11,5,2,12],
        [7,13,14,3,0,6,9,10,1,2,8,5,11,12,4,15,13,8,11,5,6,15,0,3,4,7,2,12,1,10,14,9,
         10,6,9,0,12,11,7,13,15,1,3,14,5,2,8,4,3,15,0,6,10,1,13,8,9,4,5,11,12,7,2,14],
        [2,12,4,1,7,10,11,6,8,5,3,15,13,0,14,9,14,11,2,12,4,7,13,1,5,0,15,10,3,9,8,6,
         4,2,1,11,10,13,7,8,15,9,12,5,6,3,0,14,11,8,12,7,1,14,2,13,6,15,0,9,10,4,5,3],
        [12,1,10,15,9,2,6,8,0,13,3,4,14,7,5,11,10,15,4,2,7,12,9,5,6,1,13,14,0,11,3,8,
         9,14,15,5,2,8,12,3,7,0,4,10,1,13,11,6,4,3,2,12,9,5,15,10,11,14,1,7,6,0,8,13],
        [4,11,2,14,15,0,8,13,3,12,9,7,5,10,6,1,13,0,11,7,4,9,1,10,14,3,5,12,2,15,8,6,
         1,4,11,13,12,3,7,14,10,15,6,8,0,5,9,2,6,11,13,8,1,4,10,7,9,5,0,15,14,2,3,12],
        [13,2,8,4,6,15,11,1,10,9,3,14,5,0,12,7,1,15,13,8,10,3,7,4,12,5,6,11,0,14,9,2,
         7,11,4,1,9,12,14,2,0,6,10,13,15,3,5,8,2,1,14,7,4,10,8,13,15,12,9,0,3,5,6,11]
    ];

    function bytesToBits(bytes) {
        var bits = new Uint8Array(bytes.length * 8);
        for (var i = 0; i < bytes.length; i++) {
            for (var j = 0; j < 8; j++) bits[i * 8 + j] = (bytes[i] >> (7 - j)) & 1;
        }
        return bits;
    }

    function bitsToBytes(bits) {
        var out = new Uint8Array(bits.length / 8);
        for (var i = 0; i < out.length; i++) {
            var v = 0;
            for (var j = 0; j < 8; j++) v = (v << 1) | bits[i * 8 + j];
            out[i] = v;
        }
        return out;
    }

    function permute(bits, table) {
        var out = new Uint8Array(table.length);
        for (var i = 0; i < table.length; i++) out[i] = bits[table[i] - 1];
        return out;
    }

    function desSubkeys(keyBytes) {
        var key = permute(bytesToBits(keyBytes), PC1);
        var C = key.subarray(0, 28), D = key.subarray(28, 56);
        var subkeys = [];
        var c = Array.from(C), d = Array.from(D);
        for (var r = 0; r < 16; r++) {
            for (var s = 0; s < SHIFTS[r]; s++) { c.push(c.shift()); d.push(d.shift()); }
            subkeys.push(permute(new Uint8Array(c.concat(d)), PC2));
        }
        return subkeys;
    }

    function desBlock(block8, subkeys, decrypt) {
        var bits = permute(bytesToBits(block8), IP);
        var L = Array.from(bits.subarray(0, 32)), R = Array.from(bits.subarray(32, 64));
        for (var r = 0; r < 16; r++) {
            var k = subkeys[decrypt ? 15 - r : r];
            var expanded = permute(new Uint8Array(R), E);
            var x = new Uint8Array(48);
            for (var i = 0; i < 48; i++) x[i] = expanded[i] ^ k[i];
            var sOut = new Uint8Array(32);
            for (var b = 0; b < 8; b++) {
                var chunk = x.subarray(b * 6, b * 6 + 6);
                var row = (chunk[0] << 1) | chunk[5];
                var col = (chunk[1] << 3) | (chunk[2] << 2) | (chunk[3] << 1) | chunk[4];
                var val = SBOX[b][row * 16 + col];
                for (var q = 0; q < 4; q++) sOut[b * 4 + q] = (val >> (3 - q)) & 1;
            }
            var f = permute(sOut, P);
            var newR = new Array(32);
            for (i = 0; i < 32; i++) newR[i] = L[i] ^ f[i];
            L = R; R = newR;
        }
        return bitsToBytes(permute(new Uint8Array(R.concat(L)), FP));
    }

    /**
     * 3DES-EDE CBC. `key` is 24 bytes (3-key) or 16 bytes (2-key, K3 = K1 —
     * still emitted by old keytool). A plain 8-byte key runs single DES.
     */
    function tripleDesCbc(key, iv, data, decrypt) {
        var k = u8(key), k1, k2, k3;
        if (k.length === 24) { k1 = k.subarray(0, 8); k2 = k.subarray(8, 16); k3 = k.subarray(16, 24); }
        else if (k.length === 16) { k1 = k.subarray(0, 8); k2 = k.subarray(8, 16); k3 = k.subarray(0, 8); }
        else if (k.length === 8) { k1 = k2 = k3 = k; }
        else throw new Error('3DES key must be 8, 16 or 24 bytes, got ' + k.length);

        var sk1 = desSubkeys(k1), sk2 = desSubkeys(k2), sk3 = desSubkeys(k3);
        var d = u8(data);
        if (d.length % 8) throw new Error('3DES: data not a multiple of 8 bytes');
        var out = new Uint8Array(d.length);
        var prev = u8(iv).slice(0, 8);

        for (var off = 0; off < d.length; off += 8) {
            var block = d.subarray(off, off + 8);
            if (decrypt) {
                var dec = desBlock(desBlock(desBlock(block, sk3, true), sk2, false), sk1, true);
                for (var i = 0; i < 8; i++) out[off + i] = dec[i] ^ prev[i];
                prev = block.slice();
            } else {
                var x = new Uint8Array(8);
                for (i = 0; i < 8; i++) x[i] = block[i] ^ prev[i];
                var enc = desBlock(desBlock(desBlock(x, sk1, false), sk2, true), sk3, false);
                out.set(enc, off);
                prev = enc;
            }
        }
        return out;
    }

    /* ═══ RC2 (RFC 2268) ════════════════════════════════════════════ */

    var PITABLE = new Uint8Array([
        0xd9,0x78,0xf9,0xc4,0x19,0xdd,0xb5,0xed,0x28,0xe9,0xfd,0x79,0x4a,0xa0,0xd8,0x9d,
        0xc6,0x7e,0x37,0x83,0x2b,0x76,0x53,0x8e,0x62,0x4c,0x64,0x88,0x44,0x8b,0xfb,0xa2,
        0x17,0x9a,0x59,0xf5,0x87,0xb3,0x4f,0x13,0x61,0x45,0x6d,0x8d,0x09,0x81,0x7d,0x32,
        0xbd,0x8f,0x40,0xeb,0x86,0xb7,0x7b,0x0b,0xf0,0x95,0x21,0x22,0x5c,0x6b,0x4e,0x82,
        0x54,0xd6,0x65,0x93,0xce,0x60,0xb2,0x1c,0x73,0x56,0xc0,0x14,0xa7,0x8c,0xf1,0xdc,
        0x12,0x75,0xca,0x1f,0x3b,0xbe,0xe4,0xd1,0x42,0x3d,0xd4,0x30,0xa3,0x3c,0xb6,0x26,
        0x6f,0xbf,0x0e,0xda,0x46,0x69,0x07,0x57,0x27,0xf2,0x1d,0x9b,0xbc,0x94,0x43,0x03,
        0xf8,0x11,0xc7,0xf6,0x90,0xef,0x3e,0xe7,0x06,0xc3,0xd5,0x2f,0xc8,0x66,0x1e,0xd7,
        0x08,0xe8,0xea,0xde,0x80,0x52,0xee,0xf7,0x84,0xaa,0x72,0xac,0x35,0x4d,0x6a,0x2a,
        0x96,0x1a,0xd2,0x71,0x5a,0x15,0x49,0x74,0x4b,0x9f,0xd0,0x5e,0x04,0x18,0xa4,0xec,
        0xc2,0xe0,0x41,0x6e,0x0f,0x51,0xcb,0xcc,0x24,0x91,0xaf,0x50,0xa1,0xf4,0x70,0x39,
        0x99,0x7c,0x3a,0x85,0x23,0xb8,0xb4,0x7a,0xfc,0x02,0x36,0x5b,0x25,0x55,0x97,0x31,
        0x2d,0x5d,0xfa,0x98,0xe3,0x8a,0x92,0xae,0x05,0xdf,0x29,0x10,0x67,0x6c,0xba,0xc9,
        0xd3,0x00,0xe6,0xcf,0xe1,0x9e,0xa8,0x2c,0x63,0x16,0x01,0x3f,0x58,0xe2,0x89,0xa9,
        0x0d,0x38,0x34,0x1b,0xab,0x33,0xff,0xb0,0xbb,0x48,0x0c,0x5f,0xb9,0xb1,0xcd,0x2e,
        0xc5,0xf3,0xdb,0x47,0xe5,0xa5,0x9c,0x77,0x0a,0xa6,0x20,0x68,0xfe,0x7f,0xc1,0xad
    ]);

    /**
     * RC2 key schedule. `effectiveBits` is the "effective key length" the PFX
     * declares — 40 for the ubiquitous Windows export, and it is NOT the same
     * as the key byte length, which is what makes RC2-40 files fail in naive
     * implementations that ignore the parameter.
     */
    function rc2Schedule(key, effectiveBits) {
        var T = key.length, T1 = effectiveBits || (T * 8);
        var L = new Uint8Array(128);
        L.set(key);
        var i;
        for (i = T; i < 128; i++) L[i] = PITABLE[(L[i - 1] + L[i - T]) & 0xff];
        var T8 = Math.floor((T1 + 7) / 8);
        var TM = 0xff >> ((8 - (T1 % 8)) % 8);
        L[128 - T8] = PITABLE[L[128 - T8] & TM];
        for (i = 127 - T8; i >= 0; i--) L[i] = PITABLE[L[i + 1] ^ L[i + T8]];
        var K = new Uint16Array(64);
        for (i = 0; i < 64; i++) K[i] = L[2 * i] | (L[2 * i + 1] << 8);
        return K;
    }

    function rc2Block(R, K, decrypt) {
        function rotl(x, n) { return ((x << n) | (x >>> (16 - n))) & 0xffff; }
        function rotr(x, n) { return ((x >>> n) | (x << (16 - n))) & 0xffff; }
        var s = [1, 2, 3, 5], j, i;

        if (!decrypt) {
            j = 0;
            for (var round = 0; round < 16; round++) {
                for (i = 0; i < 4; i++) {
                    R[i] = (R[i] + K[j] + (R[(i + 3) % 4] & R[(i + 2) % 4]) +
                            (~R[(i + 3) % 4] & R[(i + 1) % 4])) & 0xffff;
                    R[i] = rotl(R[i], s[i]);
                    j++;
                }
                // Mashing rounds sit after the 5th and 11th mixing rounds.
                if (round === 4 || round === 10) {
                    for (i = 0; i < 4; i++) R[i] = (R[i] + K[R[(i + 3) % 4] & 63]) & 0xffff;
                }
            }
        } else {
            j = 63;
            for (var r2 = 15; r2 >= 0; r2--) {
                if (r2 === 4 || r2 === 10) {
                    for (i = 3; i >= 0; i--) R[i] = (R[i] - K[R[(i + 3) % 4] & 63]) & 0xffff;
                }
                for (i = 3; i >= 0; i--) {
                    R[i] = rotr(R[i], s[i]);
                    R[i] = (R[i] - K[j] - (R[(i + 3) % 4] & R[(i + 2) % 4]) -
                            (~R[(i + 3) % 4] & R[(i + 1) % 4])) & 0xffff;
                    j--;
                }
            }
        }
        return R;
    }

    function rc2Cbc(key, iv, data, decrypt, effectiveBits) {
        var K = rc2Schedule(u8(key), effectiveBits);
        var d = u8(data);
        if (d.length % 8) throw new Error('RC2: data not a multiple of 8 bytes');
        var out = new Uint8Array(d.length);
        var prev = u8(iv).slice(0, 8);

        for (var off = 0; off < d.length; off += 8) {
            var block = d.subarray(off, off + 8);
            var input = block;
            if (!decrypt) {
                input = new Uint8Array(8);
                for (var i = 0; i < 8; i++) input[i] = block[i] ^ prev[i];
            }
            var R = new Uint16Array(4);
            for (i = 0; i < 4; i++) R[i] = input[i * 2] | (input[i * 2 + 1] << 8);
            rc2Block(R, K, decrypt);
            var res = new Uint8Array(8);
            for (i = 0; i < 4; i++) { res[i * 2] = R[i] & 0xff; res[i * 2 + 1] = R[i] >> 8; }
            if (decrypt) {
                for (i = 0; i < 8; i++) out[off + i] = res[i] ^ prev[i];
                prev = block.slice();
            } else {
                out.set(res, off);
                prev = res;
            }
        }
        return out;
    }

    /* ═══ RC4 (PKCS#12 pbeWithSHAAnd128BitRC4) ══════════════════════ */

    function rc4(key, data) {
        var S = new Uint8Array(256), k = u8(key), d = u8(data), i, j = 0;
        for (i = 0; i < 256; i++) S[i] = i;
        for (i = 0; i < 256; i++) {
            j = (j + S[i] + k[i % k.length]) & 0xff;
            var t = S[i]; S[i] = S[j]; S[j] = t;
        }
        var out = new Uint8Array(d.length);
        i = 0; j = 0;
        for (var n = 0; n < d.length; n++) {
            i = (i + 1) & 0xff;
            j = (j + S[i]) & 0xff;
            var tmp = S[i]; S[i] = S[j]; S[j] = tmp;
            out[n] = d[n] ^ S[(S[i] + S[j]) & 0xff];
        }
        return out;
    }

    /* ═══ KDFs ══════════════════════════════════════════════════════ */

    /**
     * PKCS#12 key derivation (RFC 7292 Appendix B.2) — not PBKDF2, a separate
     * and stranger construction that only exists inside .p12 files.
     * id: 1 = key material, 2 = IV, 3 = MAC key.
     */
    function pkcs12Kdf(password, salt, id, iterations, wantBytes, hashName) {
        var hash = hashName === 'SHA-256' ? sha256Sync : sha1;
        var u = hashName === 'SHA-256' ? 32 : 20;
        var v = 64; // block size for both SHA-1 and SHA-256

        // Password is a BMPString (UTF-16BE) WITH its terminating NUL pair.
        // Omitting that terminator is the single most common bug here — it
        // yields a plausible-looking key that decrypts to garbage.
        var pw = bmpWithTerminator(password);
        var s = u8(salt);

        var D = new Uint8Array(v);
        D.fill(id);

        var S = repeatTo(s, v), Pp = repeatTo(pw, v);
        var I = concat([S, Pp]);

        var c = Math.ceil(wantBytes / u);
        var result = new Uint8Array(c * u);

        for (var i = 0; i < c; i++) {
            var A = hash(concat([D, I]));
            for (var r = 1; r < iterations; r++) A = hash(A);
            result.set(A, i * u);
            if (i === c - 1) break;

            var B = repeatTo(A, v);
            // I is a sequence of v-byte blocks; each is incremented by B+1.
            for (var j = 0; j < I.length; j += v) {
                addInPlace(I.subarray(j, j + v), B);
            }
        }
        return result.subarray(0, wantBytes);
    }

    function bmpWithTerminator(password) {
        // A caller may hand us raw bytes instead of a string. That is how the
        // empty-password ambiguity is expressed: OpenSSL encodes "" as the
        // 2-byte NUL terminator alone, some Java versions as nothing at all,
        // and a bundle written by one convention will not open under the other.
        // Passing Uint8Array(0) selects the second reading explicitly.
        if (password instanceof Uint8Array) return password;
        var s = String(password == null ? '' : password);
        var out = new Uint8Array(s.length * 2 + 2);
        for (var i = 0; i < s.length; i++) {
            var c = s.charCodeAt(i);
            out[i * 2] = c >> 8; out[i * 2 + 1] = c & 0xff;
        }
        return out;
    }

    function repeatTo(src, blockSize) {
        if (!src.length) return new Uint8Array(0);
        var len = blockSize * Math.ceil(src.length / blockSize);
        var out = new Uint8Array(len);
        for (var i = 0; i < len; i++) out[i] = src[i % src.length];
        return out;
    }

    /** dst = (dst + src + 1) mod 2^(8*len), big-endian, in place. */
    function addInPlace(dst, src) {
        var carry = 1;
        for (var i = dst.length - 1; i >= 0; i--) {
            var sum = dst[i] + (src[i] || 0) + carry;
            dst[i] = sum & 0xff;
            carry = sum >> 8;
        }
    }

    /**
     * OpenSSL EVP_BytesToKey — the MD5 ladder behind "Proc-Type: 4,ENCRYPTED"
     * PEM keys. count is always 1 for PEM.
     */
    function evpBytesToKey(password, salt, keyLen, ivLen) {
        var pw = new TextEncoder().encode(String(password));
        var s = u8(salt);
        var material = new Uint8Array(0), prev = new Uint8Array(0);
        while (material.length < keyLen + ivLen) {
            prev = md5(concat([prev, pw, s]));
            material = concat([material, prev]);
        }
        return {
            key: material.subarray(0, keyLen),
            iv: material.subarray(keyLen, keyLen + ivLen)
        };
    }

    /* ─── a synchronous SHA-256, needed by the SHA-256 flavour of the
           PKCS#12 KDF that newer OpenSSL can emit ─────────────────── */

    var K256 = new Uint32Array([
        0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
        0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
        0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
        0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
        0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
        0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
        0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
        0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2
    ]);

    function sha256Sync(input) {
        var msg = u8(input), origLen = msg.length;
        var padLen = ((origLen + 8) >> 6 << 6) + 64;
        var buf = new Uint8Array(padLen);
        buf.set(msg);
        buf[origLen] = 0x80;
        var dv = new DataView(buf.buffer);
        var bitLen = origLen * 8;
        dv.setUint32(padLen - 8, Math.floor(bitLen / 4294967296));
        dv.setUint32(padLen - 4, bitLen >>> 0);

        var H = new Uint32Array([0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,
                                 0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19]);
        var w = new Uint32Array(64);
        for (var off = 0; off < padLen; off += 64) {
            for (var i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4);
            for (i = 16; i < 64; i++) {
                var s0 = rotr32(w[i-15],7) ^ rotr32(w[i-15],18) ^ (w[i-15] >>> 3);
                var s1 = rotr32(w[i-2],17) ^ rotr32(w[i-2],19) ^ (w[i-2] >>> 10);
                w[i] = (w[i-16] + s0 + w[i-7] + s1) >>> 0;
            }
            var a=H[0],b=H[1],c=H[2],d=H[3],e=H[4],f=H[5],g=H[6],h=H[7];
            for (i = 0; i < 64; i++) {
                var S1 = rotr32(e,6) ^ rotr32(e,11) ^ rotr32(e,25);
                var ch = (e & f) ^ (~e & g);
                var t1 = (h + S1 + ch + K256[i] + w[i]) >>> 0;
                var S0 = rotr32(a,2) ^ rotr32(a,13) ^ rotr32(a,22);
                var maj = (a & b) ^ (a & c) ^ (b & c);
                var t2 = (S0 + maj) >>> 0;
                h=g; g=f; f=e; e=(d+t1)>>>0; d=c; c=b; b=a; a=(t1+t2)>>>0;
            }
            H[0]=(H[0]+a)>>>0; H[1]=(H[1]+b)>>>0; H[2]=(H[2]+c)>>>0; H[3]=(H[3]+d)>>>0;
            H[4]=(H[4]+e)>>>0; H[5]=(H[5]+f)>>>0; H[6]=(H[6]+g)>>>0; H[7]=(H[7]+h)>>>0;
        }
        var out = new Uint8Array(32), odv = new DataView(out.buffer);
        for (i = 0; i < 8; i++) odv.setUint32(i * 4, H[i]);
        return out;
    }

    function rotr32(x, n) { return ((x >>> n) | (x << (32 - n))) >>> 0; }

    /* ═══ padding ═══════════════════════════════════════════════════ */

    function padPkcs7(data, blockSize) {
        var d = u8(data);
        var pad = blockSize - (d.length % blockSize);
        var out = new Uint8Array(d.length + pad);
        out.set(d);
        out.fill(pad, d.length);
        return out;
    }

    /**
     * Strip PKCS#7 padding. Returns null when the padding is invalid, which for
     * every caller here means "wrong password" — so the tools can say that
     * instead of showing a parse error from the mangled plaintext.
     */
    function unpadPkcs7(data, blockSize) {
        var d = u8(data);
        if (!d.length || d.length % blockSize) return null;
        var pad = d[d.length - 1];
        if (pad < 1 || pad > blockSize || pad > d.length) return null;
        for (var i = d.length - pad; i < d.length; i++) if (d[i] !== pad) return null;
        return d.subarray(0, d.length - pad);
    }

    var API = {
        md5: md5, sha1: sha1, sha256: sha256Sync,
        tripleDesCbc: tripleDesCbc, rc2Cbc: rc2Cbc, rc4: rc4,
        pkcs12Kdf: pkcs12Kdf, evpBytesToKey: evpBytesToKey,
        padPkcs7: padPkcs7, unpadPkcs7: unpadPkcs7,
        bmpWithTerminator: bmpWithTerminator
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) { root.PKI = root.PKI || {}; root.PKI.Ciphers = API; }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
